import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendUp, TrendDown, CurrencyInr } from '@phosphor-icons/react';

export default function ReportsPage() {
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Reports</h1>
      <Tabs defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales">Sales Report</TabsTrigger>
          <TabsTrigger value="doctor">Doctor-Wise</TabsTrigger>
          <TabsTrigger value="mr">MR Performance</TabsTrigger>
          <TabsTrigger value="pnl">P&L Report</TabsTrigger>
        </TabsList>
        <TabsContent value="sales"><SalesReport /></TabsContent>
        <TabsContent value="doctor"><DoctorReport /></TabsContent>
        <TabsContent value="mr"><MRReport /></TabsContent>
        <TabsContent value="pnl"><PnLReport /></TabsContent>
      </Tabs>
    </div>
  );
}

function SalesReport() {
  const [data, setData] = useState([]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  useEffect(() => { api.salesReport(month).then(r => setData(r.data)); }, [month]);
  const total = data.reduce((s, d) => s + d.total_sales, 0);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-end gap-4">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-8 mt-1 w-48" />
        </div>
        <div className="p-2 px-4 rounded-md" style={{ background: '#F0F4F8' }}>
          <span className="text-xs font-medium" style={{ color: '#8A94A6' }}>Total: </span>
          <span className="text-sm font-bold" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>₹{total.toLocaleString()}</span>
        </div>
      </div>
      <div className="rounded-md border p-4" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
            <XAxis dataKey="_id" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="total_sales" fill="#007AFF" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <thead>
            <tr style={{ background: '#F9F9FA' }}>
              {['Date', 'Orders', 'Sales', 'Received', 'Due'].map(h => (
                <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #E2E4E9' }}>
                <td className="px-4 py-2">{d._id}</td>
                <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.total_orders}</td>
                <td className="px-4 py-2 font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{d.total_sales.toLocaleString()}</td>
                <td className="px-4 py-2" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{d.total_received.toLocaleString()}</td>
                <td className="px-4 py-2" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{d.total_due.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DoctorReport() {
  const [data, setData] = useState([]);
  useEffect(() => { api.doctorWiseReport().then(r => setData(r.data)); }, []);

  return (
    <div className="mt-4 rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
      <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <thead>
          <tr style={{ background: '#F9F9FA' }}>
            {['Doctor', 'Total Orders', 'Total Sales'].map(h => (
              <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((d, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #E2E4E9' }} className="hover:bg-slate-50">
              <td className="px-4 py-2 font-medium" style={{ color: '#0A2540' }}>{d._id.doctor_name}</td>
              <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.total_orders}</td>
              <td className="px-4 py-2 font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{d.total_sales.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MRReport() {
  const [data, setData] = useState([]);
  useEffect(() => { api.mrPerformanceReport().then(r => setData(r.data)); }, []);

  return (
    <div className="mt-4 rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
      <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
        <thead>
          <tr style={{ background: '#F9F9FA' }}>
            {['MR Name', 'Attendance Days', 'Total Visits', 'Total Expenses'].map(h => (
              <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((d, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #E2E4E9' }} className="hover:bg-slate-50">
              <td className="px-4 py-2 font-medium" style={{ color: '#0A2540' }}>{d.name}</td>
              <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.attendance_days}</td>
              <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{d.total_visits}</td>
              <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{d.total_expenses.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function PnLReport() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  useEffect(() => { api.getPnlReport(month).then(r => setData(r.data)); }, [month]);

  if (!data) return <p className="mt-4" style={{ color: '#8A94A6' }}>Loading...</p>;

  const isProfit = data.net_profit >= 0;
  const pieData = [
    { name: 'COGS', value: data.cogs, color: '#007AFF' },
    { name: 'Salaries', value: data.staff_salaries, color: '#34C759' },
    { name: 'Commissions', value: data.doctor_commissions, color: '#FFCC00' },
    { name: 'Other', value: data.other_expenses, color: '#FF3B30' },
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-end gap-4">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-8 mt-1 w-48" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <div className="flex items-center gap-2 mb-1"><TrendUp size={16} style={{ color: '#007AFF' }} /><span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Revenue</span></div>
          <p className="text-2xl font-bold" style={{ color: '#0A2540', fontVariantNumeric: 'tabular-nums' }}>₹{data.revenue.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <div className="flex items-center gap-2 mb-1"><CurrencyInr size={16} style={{ color: '#34C759' }} /><span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Gross Profit</span></div>
          <p className="text-2xl font-bold" style={{ color: '#34C759', fontVariantNumeric: 'tabular-nums' }}>₹{data.gross_profit.toLocaleString()}</p>
        </div>
        <div className="p-4 rounded-md border" style={{ background: isProfit ? '#F0FFF4' : '#FFF0F0', borderColor: isProfit ? '#34C75930' : '#FF3B3030' }}>
          <div className="flex items-center gap-2 mb-1">{isProfit ? <TrendUp size={16} style={{ color: '#34C759' }} /> : <TrendDown size={16} style={{ color: '#FF3B30' }} />}<span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Net {isProfit ? 'Profit' : 'Loss'}</span></div>
          <p className="text-2xl font-bold" style={{ color: isProfit ? '#34C759' : '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{Math.abs(data.net_profit).toLocaleString()}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Breakdown Table */}
        <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
          <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <thead><tr style={{ background: '#F9F9FA' }}>
              <th className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Component</th>
              <th className="text-right px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Amount</th>
            </tr></thead>
            <tbody>
              {[
                { label: 'Revenue (Sales)', value: data.revenue, color: '#007AFF', bold: true },
                { label: 'Cost of Goods Sold (COGS)', value: -data.cogs, color: '#FF3B30' },
                { label: 'Gross Profit', value: data.gross_profit, color: '#34C759', bold: true, separator: true },
                { label: 'Staff Salaries', value: -data.staff_salaries, color: '#FF3B30' },
                { label: 'Doctor Commissions', value: -data.doctor_commissions, color: '#FF3B30' },
                { label: 'Other Expenses', value: -data.other_expenses, color: '#FF3B30' },
                { label: `Net ${isProfit ? 'Profit' : 'Loss'}`, value: data.net_profit, color: isProfit ? '#34C759' : '#FF3B30', bold: true, separator: true },
              ].map((row, i) => (
                <tr key={i} style={{ borderTop: row.separator ? '2px solid #0A2540' : '1px solid #E2E4E9' }}>
                  <td className="px-4 py-2" style={{ fontWeight: row.bold ? 700 : 400, color: '#0A2540' }}>{row.label}</td>
                  <td className="px-4 py-2 text-right font-medium" style={{ color: row.color, fontVariantNumeric: 'tabular-nums', fontWeight: row.bold ? 700 : 500 }}>
                    {row.value < 0 ? '-' : ''}₹{Math.abs(row.value).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Expense Breakdown Pie */}
        {pieData.length > 0 && (
          <div className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            <h3 className="text-sm font-semibold mb-2" style={{ color: '#0A2540' }}>Expense Breakdown</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
